import React, { useState } from "react";
import { utils, writeFile } from "xlsx";

function PurchaseReport() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [purchaseData, setPurchaseData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(""); // For date filter

  // Format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Fetch purchase report data
  const fetchPurchaseReport = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates.");
      return;
    }

    const payload = { start_date: startDate, end_date: endDate };

    fetch("https://stock.krishtec.co.in/stock/get_purchase_report.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        console.log("Purchase Report Data:", data);
        setPurchaseData(Array.isArray(data) ? data : []);
        setSelectedDate(""); // Reset date filter on new fetch
      })
      .catch((err) => {
        console.log("Fetch purchase report error:", err);
        setPurchaseData([]);
      });
  };

  // Get unique purchase dates for filter dropdown
  const uniqueDates = [...new Set(purchaseData.map((item) => item.purchase_date))].sort();

  // Filter purchase data based on search term and selected date
  const filteredPurchaseData = purchaseData.filter((item) => {
    const matchesSearch = item.product_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDate = selectedDate ? item.purchase_date === selectedDate : true;
    return matchesSearch && matchesDate;
  });

  // Download report as Excel
  const downloadExcel = () => {
    if (filteredPurchaseData.length === 0) {
      alert("No data to download.");
      return;
    }

    const worksheetData = filteredPurchaseData.map((item) => ({
      "Purchase Date": formatDate(item.purchase_date),
      "Product ID": item.product_id,
      "Product Name": item.product_name,
      "Quantity Purchased": item.quantity_purchased,
      "Price Per Unit (Rs.)": parseFloat(item.price_per_unit).toFixed(2),
      "Total Purchase Amount (Rs.)": parseFloat(item.total_purchase_amount).toFixed(2),
      "Vendor": item.vendor,
    }));

    const worksheet = utils.json_to_sheet(worksheetData);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Purchase Report");
    worksheet["!cols"] = [
      { wch: 12 }, // Purchase Date
      { wch: 12 }, // Product ID
      { wch: 20 }, // Product Name
      { wch: 15 }, // Quantity Purchased
      { wch: 15 }, // Price Per Unit
      { wch: 20 }, // Total Purchase Amount
      { wch: 15 }, // Vendor
    ];
    writeFile(workbook, `Purchase_Report_${startDate}_to_${endDate}.xlsx`);
  };

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center p-6">
      <div className="flex flex-col gap-6 w-full max-w-6xl">
        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-900 text-center">Purchase Report</h1>

        {/* Date Range Inputs, Search Bar, and Date Filter */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex gap-4 items-end">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5 w-40"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5 w-40"
              />
            </div>
            <button
              onClick={fetchPurchaseReport}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-1.5 px-3 rounded-lg transition duration-200"
            >
              Fetch
            </button>
          </div>
          <div className="flex items-end gap-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Filter by Date</label>
              <select
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5 w-40"
              >
                <option value="">All Dates</option>
                {uniqueDates.map((date) => (
                  <option key={date} value={date}>
                    {formatDate(date)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Search Product</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by product name..."
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5 w-64"
              />
            </div>
            <button
              onClick={downloadExcel}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            >
              Download Excel
            </button>
          </div>
        </div>

        {/* Purchase Report Table */}
        {filteredPurchaseData.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Purchase Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Product ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Product Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Quantity Purchased
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Price Per Unit (Rs.)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Total Purchase Amount (Rs.)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Vendor
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredPurchaseData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                      {formatDate(item.purchase_date)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{item.product_id}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{item.product_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{item.quantity_purchased}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      {parseFloat(item.price_per_unit).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      {parseFloat(item.total_purchase_amount).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">{item.vendor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          purchaseData.length === 0 && (
            <p className="text-center text-gray-500 mt-4">No data available. Please fetch the report.</p>
          )
        )}
      </div>
    </div>
  );
}

export default PurchaseReport;